import {createContext, useContext, useState, useEffect, useCallback, FC, ReactNode} from 'react'
import {useLocation} from 'react-router-dom'

type SidebarContextValue = {
  isDrawerOpen: boolean
  openDrawer: () => void
  closeDrawer: () => void
  isMinimized: boolean
  toggleMinimized: () => void
}

const SidebarContext = createContext<SidebarContextValue>({
  isDrawerOpen: false,
  openDrawer: () => {},
  closeDrawer: () => {},
  isMinimized: false,
  toggleMinimized: () => {},
})

const MINIMIZED_KEY = 'sidebar_minimized'

const SidebarProvider: FC<{children: ReactNode}> = ({children}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isMinimized, setIsMinimized] = useState(() => localStorage.getItem(MINIMIZED_KEY) === '1')
  const {pathname} = useLocation()

  const openDrawer = useCallback(() => setIsDrawerOpen(true), [])
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), [])
  const toggleMinimized = useCallback(() => {
    setIsMinimized((prev) => {
      localStorage.setItem(MINIMIZED_KEY, prev ? '0' : '1')
      return !prev
    })
  }, [])

  // navegou → fecha o drawer
  useEffect(() => {
    setIsDrawerOpen(false)
  }, [pathname])

  // ESC fecha o drawer
  useEffect(() => {
    if (!isDrawerOpen) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setIsDrawerOpen(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isDrawerOpen])

  // o wrapper do Metronic ajusta o padding-left a partir deste atributo do body
  useEffect(() => {
    if (isMinimized) {
      document.body.setAttribute('data-kt-aside-minimize', 'on')
    } else {
      document.body.removeAttribute('data-kt-aside-minimize')
    }
  }, [isMinimized])

  return (
    <SidebarContext.Provider
      value={{isDrawerOpen, openDrawer, closeDrawer, isMinimized, toggleMinimized}}
    >
      {children}
    </SidebarContext.Provider>
  )
}

const useSidebar = () => useContext(SidebarContext)

export {SidebarProvider, useSidebar}
